import {InfiniteData, useQueryClient} from 'react-query';

export interface PaginationDataProps<T> {
  data: T[];
  isLastPage: boolean;
  count: number;
  currentPage: number;
}

const useInfiniteQueryHelpers = () => {
  const queryClient = useQueryClient();

  const removeQueries = (key: any) => queryClient.removeQueries(key);

  const getData = <T>(key: any) => {
    const data = queryClient.getQueryData(key) as InfiniteData<
      PaginationDataProps<T>
    >;

    return data?.pages?.reduce(
      (data: T[], item) => [...data, ...item.data],
      [],
    );
  };

  const isQuery = (key: any) => {
    const queries = queryClient.getQueriesData<InfiniteData<any>>(key);

    if (!Array.isArray(queries[0])) return;

    return queries[0];
  };

  const isQueries = (key: any) => {
    const queries = queryClient.getQueriesData<InfiniteData<any>>(key);

    if (!Array.isArray(queries[0])) return;

    return queries;
  };

  const create = (key: any, newItem: any) => {
    const infiniteQueries = isQueries(key);

    if (!infiniteQueries) return;

    infiniteQueries.forEach((inifiteQuery) => {
      if (!inifiteQuery) return;

      const [queryKey, data] = inifiteQuery;

      if (!data) return;

      const lastIndex = data.pages.length - 1;

      const pages = data.pages.map(({data, ...rest}, index) => {
        return {
          ...rest,
          data: index === lastIndex ? [...data, newItem] : data,
        };
      });

      const inifiteData = {...data, pages};

      queryClient.setQueriesData(queryKey, inifiteData);
    });
  };

  const update = (key: any, newItem: any) => {
    const infiniteQueries = isQueries(key);

    if (!infiniteQueries) return;

    infiniteQueries.forEach((inifiteQuery) => {
      if (!inifiteQuery) return;

      const [queryKey, data] = inifiteQuery;

      if (!data) return;

      const pages = data.pages.map(({data, ...rest}) => ({
        ...rest,
        data: data.map((item: any) =>
          item.id === newItem.id ? {...item, ...newItem} : item,
        ),
      }));

      const inifiteData = {...data, pages};

      queryClient.setQueriesData(queryKey, inifiteData);
    });
  };

  const updateByCondition = <T>(
    key: any,
    condition: (data: T) => boolean,
    newItem: any,
  ) => {
    const inifityQuery = isQuery(key);

    if (!inifityQuery) return;

    const [queryKey, data] = inifityQuery;

    if (!data) return;

    const pages = data.pages.map(({data, ...rest}) => ({
      ...rest,
      data: data.map((item: any) =>
        condition(item) ? {...item, ...newItem} : item,
      ),
    }));

    const inifityData = {...data, pages};

    queryClient.setQueriesData(queryKey, inifityData);
  };

  const remove = (key: any, removeItem: any) => {
    const infiniteQueries = isQueries(key);

    if (!infiniteQueries) return;

    infiniteQueries.forEach((inifiteQuery) => {
      if (!inifiteQuery) return;

      const [queryKey, data] = inifiteQuery;

      if (!data) return;

      const pages = data.pages.map(({data, ...rest}) => ({
        ...rest,
        data: data.filter((item: any) => item.id !== removeItem.id),
      }));

      const inifiteData = {...data, pages};

      queryClient.setQueriesData(queryKey, inifiteData);
    });
  };

  return {
    create,
    update,
    remove,
    getData,
    updateByCondition,
    removeQueries,
    invalidateQueries: queryClient.invalidateQueries,
  };
};

export default useInfiniteQueryHelpers;
